/* Môdern Studio — AI Budget Planner
 * Slot-based budget allocation. Instead of listing every product under the cap,
 * the planner splits the budget into room-appropriate slots (anchor piece,
 * seating, rug, lighting, decor...) and proposes one complete room, which the
 * shopper then swaps piece by piece.
 *
 * Allocation shares follow standard designer guidance: one anchor piece takes
 * the largest share, accessories take a small slice. Shares are re-normalised
 * whenever a slot is dropped or cannot be filled, so the budget is always
 * fully spendable.
 */
(function () {
  var root = document.querySelector('[data-budget-tool]');
  if (!root) return;

  var DEBUG = /(?:\?|&)msgdebug=1(?:&|$)/.test(window.location.search);
  function log() {
    if (!DEBUG) return;
    try { console.log.apply(console, ['[budget-planner]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  /* ---------------------------------------------------------------------
   * Room configuration
   * `share` values are percentages of the budget and sum to 100 per room.
   * `required: true` slots are never dropped; optional slots are dropped
   * first when the budget is too small to cover everything.
   * `types` are matched against Shopify product_type values.
   * ------------------------------------------------------------------ */
  var ROOMS = {
    'living-room-collection': {
      label: 'Living Room',
      min: 400, defaultBudget: 1500,
      slots: [
        { key: 'anchor',   name: 'Anchor seating', share: 40, required: true,
          types: ['Sofas', 'Sectional Sofas', 'Loveseats', 'Daybeds & Futons', 'Chaise Lounges'] },
        { key: 'seating',  name: 'Accent seating', share: 15,
          types: ['Accent Chairs', 'Recliners & Armchairs', 'Ottomans & Poufs', 'Benches'] },
        { key: 'tables',   name: 'Tables', share: 10,
          types: ['Coffee Tables', 'Side & End Tables', 'Console Tables'] },
        { key: 'rug',      name: 'Rug & textiles', share: 15,
          types: ['Rugs', 'Throw Pillows', 'Pillow Covers'] },
        { key: 'lighting', name: 'Lighting', share: 10,
          types: ['Table & Floor Lamps', 'Ceiling & Pendant Lighting'] },
        { key: 'decor',    name: 'Decor', share: 10,
          types: ['Wall Art', 'Mirrors', 'Faux Plants & Trees', 'Vases', 'Sculptures & Objects'] }
      ]
    },
    'bedroom-collection': {
      label: 'Bedroom',
      min: 450, defaultBudget: 1500,
      slots: [
        { key: 'bed',      name: 'Bed frame', share: 28, required: true,
          types: ['Beds & Bed Frames', 'Headboards'] },
        { key: 'mattress', name: 'Mattress', share: 24, required: true,
          types: ['Mattresses'] },
        { key: 'storage',  name: 'Storage', share: 14,
          types: ['Dressers', 'Cabinets & Sideboards', 'Storage Chests'] },
        { key: 'night',    name: 'Nightstands', share: 9,
          types: ['Nightstands', 'Side & End Tables'] },
        { key: 'bedding',  name: 'Bedding', share: 9,
          types: ['Bedding Sets', 'Sheets & Sheet Sets'] },
        { key: 'rug',      name: 'Rug', share: 8,
          types: ['Rugs'] },
        { key: 'lighting', name: 'Lighting & decor', share: 8,
          types: ['Table & Floor Lamps', 'Ceiling & Pendant Lighting', 'Mirrors', 'Wall Art', 'Throw Pillows'] }
      ]
    },
    'office-furniture': {
      label: 'Home Office',
      min: 250, defaultBudget: 900,
      slots: [
        { key: 'desk',     name: 'Desk', share: 35, required: true,
          types: ['Desks'] },
        { key: 'chair',    name: 'Chair', share: 30, required: true,
          types: ['Office Chairs', 'Accent Chairs'] },
        { key: 'storage',  name: 'Storage', share: 20,
          types: ['Bookcases & Shelving'] },
        { key: 'rug',      name: 'Rug & lighting', share: 10,
          types: ['Rugs', 'Table & Floor Lamps'] },
        { key: 'decor',    name: 'Decor', share: 5,
          types: ['Wall Art', 'Faux Plants & Trees'] }
      ]
    },
    'kitchen-collection': {
      label: 'Kitchen Upgrade',
      min: 200, defaultBudget: 800,
      note: 'This room is built around appliances and tableware rather than furniture, so the budget is split across an upgrade rather than a full furnishing.',
      slots: [
        { key: 'major',    name: 'Major appliance', share: 40, required: true,
          types: ['Refrigerators', 'Chest Freezers', 'Beverage & Wine Coolers', 'Ice Makers', 'Mini Fridges'] },
        { key: 'counter',  name: 'Counter appliance', share: 25, required: true,
          types: ['Coffee & Espresso Machines', 'Air Fryers', 'Blenders & Mixers', 'Bread Makers',
                  'Toasters & Toaster Ovens', 'Electric Kettles', 'Juicers', 'Specialty Kitchen Appliances',
                  'Ice Cream Makers', 'Pasta Makers', 'Tea Makers', 'Yogurt Makers'] },
        { key: 'cook',     name: 'Cookware & knives', share: 15,
          types: ['Cookware', 'Knives & Knife Storage', 'Cutting Boards'] },
        { key: 'table',    name: 'Tableware', share: 12,
          types: ['Dinnerware', 'Flatware', 'Bowls', 'Drinking Glasses', 'Wine Glasses & Stemware',
                  'Mugs', 'Drinkware', 'Teapots & Coffee Servers'] },
        { key: 'dining',   name: 'Dining seating', share: 8,
          types: ['Dining Chairs', 'Stools & Counter Stools', 'Dining Tables', 'Dining Sets'] }
      ]
    },
    'bathroom-collection': {
      label: 'Bathroom',
      min: 60, max: 400, defaultBudget: 250,
      note: 'Bathroom pieces are small-ticket items, so this room is capped at $400 — above that there is simply nothing left to spend it on.',
      slots: [
        { key: 'storage',  name: 'Storage', share: 25, required: true,
          types: ['Bath Storage', 'Baskets & Bins', 'Wall Organizers'] },
        { key: 'towels',   name: 'Towels', share: 25, required: true,
          types: ['Bath Towels'] },
        { key: 'mat',      name: 'Bath mat', share: 15,
          types: ['Bath Mats'] },
        { key: 'curtain',  name: 'Shower curtain', share: 15,
          types: ['Shower Curtains'] },
        { key: 'access',   name: 'Accessories', share: 20,
          types: ['Bath Accessories', 'Soap Dispensers', 'Towel Bars & Hooks', 'Home Fragrance', 'Body Scales'] }
      ]
    },
    'outdoor-furniture': {
      label: 'Outdoor',
      min: 300, defaultBudget: 1200,
      modes: [
        { key: 'set', name: 'One complete set',
          slots: [
            { key: 'set', name: 'Patio set', share: 100, required: true,
              types: ['Patio Furniture Sets', 'Patio Dining Sets'] }
          ] },
        { key: 'pieces', name: 'Build it piece by piece',
          slots: [
            { key: 'seating', name: 'Seating', share: 45, required: true,
              types: ['Patio Seating', 'Patio Benches', 'Loveseats', 'Sofas', 'Daybeds & Futons', 'Patio Ottomans'] },
            { key: 'table',   name: 'Table', share: 20,
              types: ['Patio Tables', 'Dining Tables'] },
            { key: 'feature', name: 'Fire pit or grill', share: 25,
              types: ['Fire Pits', 'Grills', 'Food Smokers'] },
            { key: 'decor',   name: 'Decor & storage', share: 10,
              types: ['Planters & Plant Stands', 'Water Features', 'Table & Floor Lamps',
                      'Outdoor Storage', 'Storage Boxes', 'Coolers'] }
          ] }
      ]
    }
  };

  /* --------------------------- DOM handles --------------------------- */
  var setupStep   = root.querySelector('[data-budget-step="setup"]');
  var planStep    = root.querySelector('[data-budget-step="plan"]');
  var roomGrid    = root.querySelector('[data-budget-room-grid]');
  var amountInput = root.querySelector('[data-budget-amount]');
  var startBtn    = root.querySelector('[data-budget-start]');
  var hintEl      = root.querySelector('[data-budget-hint]');
  var rangeEl     = root.querySelector('[data-budget-range]');
  var modeRow     = root.querySelector('[data-budget-mode-row]');
  var roomLabelEl = root.querySelector('[data-budget-room-label]');
  var totalBar    = root.querySelector('[data-budget-total-bar]');
  var totalEl     = root.querySelector('[data-budget-total]');
  var capEl       = root.querySelector('[data-budget-cap]');
  var fillEl      = root.querySelector('[data-budget-fill]');
  var segBar      = root.querySelector('[data-budget-segments]');
  var slotList    = root.querySelector('[data-budget-slots]');
  var loadingEl   = root.querySelector('[data-budget-loading]');
  var restartBtn  = root.querySelector('[data-budget-restart]');
  var saveBtn     = root.querySelector('[data-budget-save]');
  var saveNoteEl  = root.querySelector('[data-budget-save-note]');
  var noteEl      = root.querySelector('[data-budget-note]');

  /* --------------------------- State --------------------------- */
  var room = null;          // resolved room config (mode applied)
  var roomHandle = null;
  var roomMode = null;
  var budgetCap = 1000;
  var pool = [];            // all fetched products
  var plan = [];            // [{slot, pick, alternates, included}]

  /* --------------------------- Helpers --------------------------- */
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  // Shopify CDN URLs usually carry a ?v= param, but not always. Appending a
  // bare "&width=" to a src with no query string yields a broken image.
  function sized(src, w) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  function money(n) {
    return '$' + Number(n || 0).toFixed(2);
  }
  function money0(n) {
    return '$' + Math.round(Number(n || 0)).toLocaleString();
  }

  function activeSlots() {
    return room.slots;
  }

  /* --------------------------- Room picking --------------------------- */
  Array.prototype.forEach.call(roomGrid.querySelectorAll('.budget-room'), function (btn) {
    btn.addEventListener('click', function () {
      Array.prototype.forEach.call(roomGrid.querySelectorAll('.budget-room'), function (b) {
        b.classList.remove('is-selected');
      });
      btn.classList.add('is-selected');
      roomHandle = btn.getAttribute('data-collection');
      var cfg = ROOMS[roomHandle];
      roomMode = cfg && cfg.modes ? cfg.modes[0].key : null;
      applyRoomBudgetBounds(cfg);
      renderModeChoice(cfg);
      startBtn.disabled = false;
      hintEl.hidden = true;
    });
  });

  function applyRoomBudgetBounds(cfg) {
    if (!cfg) return;
    amountInput.min = cfg.min;
    if (cfg.max) { amountInput.max = cfg.max; } else { amountInput.removeAttribute('max'); }
    amountInput.value = cfg.defaultBudget;
    if (rangeEl) {
      rangeEl.hidden = false;
      rangeEl.textContent = cfg.max
        ? 'Between ' + money0(cfg.min) + ' and ' + money0(cfg.max) + ' for this room.'
        : 'Minimum ' + money0(cfg.min) + ' for a complete ' + cfg.label.toLowerCase() + '.';
    }
  }

  function renderModeChoice(cfg) {
    if (!modeRow) return;
    if (!cfg || !cfg.modes) { modeRow.hidden = true; modeRow.innerHTML = ''; return; }
    modeRow.hidden = false;
    modeRow.innerHTML = '<span class="budget-mode__label">How would you like to shop it?</span>' +
      cfg.modes.map(function (m, i) {
        return '<button type="button" class="budget-mode' + (i === 0 ? ' is-selected' : '') +
          '" data-mode="' + esc(m.key) + '">' + esc(m.name) + '</button>';
      }).join('');
    Array.prototype.forEach.call(modeRow.querySelectorAll('.budget-mode'), function (b) {
      b.addEventListener('click', function () {
        Array.prototype.forEach.call(modeRow.querySelectorAll('.budget-mode'), function (x) {
          x.classList.remove('is-selected');
        });
        b.classList.add('is-selected');
        roomMode = b.getAttribute('data-mode');
      });
    });
  }

  /* --------------------------- Start --------------------------- */
  startBtn.addEventListener('click', function () {
    if (!roomHandle) { hintEl.hidden = false; return; }
    var cfg = ROOMS[roomHandle];
    if (!cfg) { hintEl.hidden = false; return; }

    var requested = parseFloat(amountInput.value) || cfg.defaultBudget;
    budgetCap = Math.max(cfg.min, cfg.max ? Math.min(cfg.max, requested) : requested);
    amountInput.value = budgetCap;

    // Resolve the room config for the chosen mode.
    var slots;
    if (cfg.modes) {
      var m = cfg.modes.filter(function (x) { return x.key === roomMode; })[0] || cfg.modes[0];
      slots = m.slots;
    } else {
      slots = cfg.slots;
    }
    room = { label: cfg.label, note: cfg.note, slots: slots };

    roomLabelEl.textContent = room.label;
    capEl.textContent = money(budgetCap);
    if (noteEl) {
      if (room.note) { noteEl.hidden = false; noteEl.textContent = room.note; }
      else { noteEl.hidden = true; noteEl.textContent = ''; }
    }
    if (saveNoteEl) saveNoteEl.hidden = true;
    setupStep.hidden = true;
    planStep.hidden = false;
    loadPool(roomHandle);
  });

  restartBtn.addEventListener('click', function () {
    planStep.hidden = true;
    setupStep.hidden = false;
  });

  /* --------------------------- Data loading ---------------------------
   * products.json caps at 250 per page, and several room collections now
   * hold more than that, so pages are walked until one comes back short.
   * ------------------------------------------------------------------ */
  function loadPool(handle) {
    loadingEl.hidden = false;
    slotList.innerHTML = '';
    if (segBar) segBar.innerHTML = '';
    pool = [];

    var MAX_PAGES = 8;

    function page(n) {
      return fetch('/collections/' + handle + '/products.json?limit=250&page=' + n)
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (data) {
          var batch = data.products || [];
          batch.forEach(function (p) {
            var v = p.variants && p.variants[0];
            var price = v ? parseFloat(v.price) : 0;
            var tags = p.tags || [];
            var excluded = false;
            for (var i = 0; i < tags.length; i++) {
              if (String(tags[i]).toLowerCase() === 'studio-exclude') { excluded = true; break; }
            }
            if (excluded || !(price > 0)) return;
            pool.push({
              id: String(p.id),
              title: p.title,
              handle: p.handle,
              url: '/products/' + p.handle,
              price: price,
              type: p.product_type || '',
              image: p.images && p.images[0] ? p.images[0].src : '',
              available: v ? v.available !== false : true
            });
          });
          if (batch.length === 250 && n < MAX_PAGES) return page(n + 1);
          return null;
        });
    }

    page(1).then(function () {
      loadingEl.hidden = true;
      log('pool loaded', pool.length, 'products for', handle);
      buildPlan();
      renderPlan();
    }).catch(function (err) {
      loadingEl.hidden = true;
      log('load failed', err);
      slotList.innerHTML = '<p class="budget-empty">Could not load products for this room right now. Please try again.</p>';
    });
  }

  /* --------------------------- Allocation ---------------------------
   * Two passes:
   *  1. Walk slots from the largest share down. Each slot's target is its
   *     share of whatever budget is still unclaimed, so an under-spend on
   *     one slot immediately raises the targets of the slots after it.
   *  2. If money is still left over, upgrade the largest slot to the best
   *     candidate that fits.
   * Optional slots with no inventory are dropped and their share is
   * redistributed rather than wasted.
   * ------------------------------------------------------------------ */
  function candidatesFor(slot, usedIds) {
    var out = [];
    for (var i = 0; i < pool.length; i++) {
      var p = pool[i];
      if (usedIds[p.id]) continue;
      if (!p.available) continue;
      if (slot.types.indexOf(p.type) === -1) continue;
      out.push(p);
    }
    out.sort(function (a, b) { return a.price - b.price; });
    return out;
  }

  function nearest(cands, target, ceiling) {
    if (!cands.length) return null;
    var eligible = cands.filter(function (c) { return c.price <= ceiling; });
    if (!eligible.length) return null;
    var lo = target * 0.55, hi = target * 1.45;
    var inBand = eligible.filter(function (c) { return c.price >= lo && c.price <= hi; });
    var group = inBand.length ? inBand : eligible;
    var best = group[0];
    var bestD = Math.abs(best.price - target);
    for (var i = 1; i < group.length; i++) {
      var d = Math.abs(group[i].price - target);
      if (d < bestD) { best = group[i]; bestD = d; }
    }
    return best;
  }

  function buildPlan() {
    var slots = activeSlots().slice();
    var usedIds = {};
    plan = [];

    // Order by share descending so the anchor is chosen first.
    var ordered = slots.slice().sort(function (a, b) { return b.share - a.share; });

    // Drop slots with zero inventory, redistribute their share.
    var live = [];
    var lostShare = 0;
    ordered.forEach(function (s) {
      if (candidatesFor(s, {}).length) { live.push(s); }
      else { lostShare += s.share; log('slot dropped, no inventory:', s.name); }
    });
    if (!live.length) return;
    var totalShare = live.reduce(function (a, s) { return a + s.share; }, 0);
    if (lostShare > 0 && totalShare > 0) {
      live = live.map(function (s) {
        return { key: s.key, name: s.name, required: s.required, types: s.types,
                 share: s.share + (lostShare * (s.share / totalShare)) };
      });
    }

    var remainingBudget = budgetCap;
    var remainingShare = live.reduce(function (a, s) { return a + s.share; }, 0);

    live.forEach(function (s) {
      var target = remainingShare > 0 ? remainingBudget * (s.share / remainingShare) : 0;
      var cands = candidatesFor(s, usedIds);
      // Ceiling: this slot's target plus a slice of headroom, never the whole
      // remaining budget, or the first slot would eat everything.
      var ceiling = Math.max(target * 1.6, target + (remainingBudget - target) * 0.25);
      var pick = nearest(cands, target, ceiling);
      if (!pick && cands.length) {
        var cheapest = cands[0];
        if (cheapest.price <= remainingBudget) pick = cheapest;
      }
      var entry = {
        slot: s, target: target, pick: pick || null, included: !!pick,
        alternates: []
      };
      if (pick) {
        usedIds[pick.id] = true;
        remainingBudget -= pick.price;
        entry.alternates = cands.filter(function (c) { return c.id !== pick.id; })
          .sort(function (a, b) { return Math.abs(a.price - target) - Math.abs(b.price - target); })
          .slice(0, 6);
      } else {
        log('slot unfilled:', s.name, 'target', target);
      }
      remainingShare -= s.share;
      plan.push(entry);
    });

    // Pass 2 — spend meaningful leftovers by upgrading the biggest slot.
    var leftover = budgetCap - planTotal();
    if (leftover > budgetCap * 0.12) {
      for (var i = 0; i < plan.length; i++) {
        var e = plan[i];
        if (!e.pick) continue;
        var room2 = e.pick.price + leftover;
        var better = null;
        for (var j = 0; j < e.alternates.length; j++) {
          var c = e.alternates[j];
          if (c.price <= room2 && c.price > e.pick.price && (!better || c.price > better.price)) better = c;
        }
        if (better) {
          log('upgrade', e.slot.name, e.pick.price, '->', better.price);
          e.alternates.push(e.pick);
          e.alternates = e.alternates.filter(function (c) { return c.id !== better.id; })
            .sort(function (a, b) { return Math.abs(a.price - e.target) - Math.abs(b.price - e.target); })
            .slice(0, 6);
          e.pick = better;
          break;
        }
      }
    }

    // Pass 3 — get under the cap.
    fitToBudget();

    // Restore original display order.
    var order = {};
    activeSlots().forEach(function (s, i) { order[s.key] = i; });
    plan.sort(function (a, b) { return (order[a.slot.key] || 0) - (order[b.slot.key] || 0); });
  }

  /* Bring the plan under the cap with the least damage.
   * Downgrading one piece to a cheaper alternate is always preferable to
   * dropping a whole category: overshooting by a couple of dollars should
   * not cost the shopper their rug or their lamp. Slots are only dropped
   * when no combination of downgrades can close the gap.
   */
  function swapPick(entry, replacement) {
    var previous = entry.pick;
    entry.pick = replacement;
    entry.alternates = entry.alternates.filter(function (c) { return c.id !== replacement.id; });
    if (previous) entry.alternates.push(previous);
    entry.alternates.sort(function (a, b) {
      return Math.abs(a.price - entry.target) - Math.abs(b.price - entry.target);
    });
    entry.alternates = entry.alternates.slice(0, 6);
  }

  function fitToBudget() {
    var guard = 0;
    while (planTotal() > budgetCap && guard < 40) {
      guard++;
      var over = planTotal() - budgetCap;

      // Prefer the smallest downgrade that closes the whole gap; failing
      // that, take the biggest available saving and go round again.
      var best = null;
      plan.forEach(function (e) {
        if (!e.included || !e.pick) return;
        e.alternates.forEach(function (c) {
          if (c.price >= e.pick.price) return;
          var saving = e.pick.price - c.price;
          var score = saving >= over ? (saving - over) : (1e9 - saving);
          if (!best || score < best.score) best = { entry: e, cand: c, score: score, saving: saving };
        });
      });
      if (best) {
        log('downgrade to fit:', best.entry.slot.name,
            best.entry.pick.price, '->', best.cand.price);
        swapPick(best.entry, best.cand);
        continue;
      }

      // Nothing left to downgrade — drop the smallest optional slot.
      var victim = null;
      for (var k = plan.length - 1; k >= 0; k--) {
        var pe = plan[k];
        if (pe.included && pe.pick && !pe.slot.required) {
          if (!victim || pe.pick.price < victim.pick.price) victim = pe;
        }
      }
      if (!victim) break;
      victim.included = false;
      log('dropped to fit budget:', victim.slot.name);
    }
  }

  function planTotal() {
    return plan.reduce(function (a, e) {
      return a + (e.included && e.pick ? e.pick.price : 0);
    }, 0);
  }

  /* --------------------------- Rendering --------------------------- */
  function renderPlan() {
    if (!plan.length) {
      slotList.innerHTML = '<p class="budget-empty">No products are available for this room right now.</p>';
      return;
    }

    slotList.innerHTML = plan.map(function (e, idx) {
      var s = e.slot;
      var pct = Math.round(s.share);
      var head = '<div class="budget-slot__head">' +
        '<label class="budget-slot__toggle">' +
          '<input type="checkbox" class="budget-slot__checkbox" data-slot-idx="' + idx + '"' +
            (e.included ? ' checked' : '') + (e.pick ? '' : ' disabled') + '>' +
          '<span class="budget-slot__name">' + esc(s.name) + '</span>' +
        '</label>' +
        '<span class="budget-slot__share">' + pct + '% &middot; about ' + money0(e.target) + '</span>' +
      '</div>';

      if (!e.pick) {
        return '<div class="budget-slot is-empty">' + head +
          '<p class="budget-slot__empty">Nothing in this range right now — the budget was shared across the other pieces.</p>' +
        '</div>';
      }

      var p = e.pick;
      var title = esc(p.title);
      var card = '<div class="budget-card" data-id="' + esc(p.id) + '">' +
        '<div class="budget-card__image-wrap">' +
          (p.image ? '<img src="' + esc(sized(p.image, 400)) + '" loading="lazy" alt="' + title + '">' : '') +
        '</div>' +
        '<div class="budget-card__body">' +
          '<div class="budget-card__type">' + esc(p.type) + '</div>' +
          '<div class="budget-card__title">' + title + '</div>' +
          '<div class="budget-card__price">' + money(p.price) + '</div>' +
          '<a class="budget-card__link" href="' + esc(p.url) + '">' +
            '<span class="budget-card__link-title">' + title + '</span>View product &rarr;' +
          '</a>' +
        '</div>' +
      '</div>';

      var alts = '';
      if (e.alternates.length) {
        alts = '<div class="budget-alts">' +
          '<span class="budget-alts__label">Swap for</span>' +
          '<div class="budget-alts__row">' +
          e.alternates.map(function (c) {
            return '<button type="button" class="budget-alt" data-slot-idx="' + idx + '" data-alt-id="' + esc(c.id) + '">' +
              (c.image ? '<img src="' + esc(sized(c.image, 120)) + '" loading="lazy" alt="">' : '') +
              '<span class="budget-alt__price">' + money(c.price) + '</span>' +
            '</button>';
          }).join('') +
          '</div></div>';
      }

      return '<div class="budget-slot' + (e.included ? '' : ' is-excluded') + '">' +
        head + card + alts + '</div>';
    }).join('');

    // Toggle a slot in or out of the plan.
    Array.prototype.forEach.call(slotList.querySelectorAll('.budget-slot__checkbox'), function (cb) {
      cb.addEventListener('change', function () {
        var e = plan[parseInt(cb.getAttribute('data-slot-idx'), 10)];
        if (!e) return;
        e.included = cb.checked;
        var wrap = cb.closest('.budget-slot');
        if (wrap) wrap.classList.toggle('is-excluded', !cb.checked);
        updateTotals();
      });
    });

    // Swap the pick for an alternate.
    Array.prototype.forEach.call(slotList.querySelectorAll('.budget-alt'), function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(btn.getAttribute('data-slot-idx'), 10);
        var e = plan[idx];
        if (!e) return;
        var id = btn.getAttribute('data-alt-id');
        var chosen = e.alternates.filter(function (c) { return c.id === id; })[0];
        if (!chosen) return;
        swapPick(e, chosen);
        log('swapped', e.slot.name, '->', chosen.title, chosen.price);
        renderPlan();
        updateTotals();
      });
    });

    updateTotals();
  }

  function renderSegments() {
    if (!segBar) return;
    var total = planTotal();
    var denom = Math.max(total, budgetCap);
    var html = plan.filter(function (e) { return e.included && e.pick; }).map(function (e) {
      var w = denom > 0 ? (e.pick.price / denom) * 100 : 0;
      return '<span class="budget-seg" style="width:' + w.toFixed(2) + '%" ' +
        'title="' + esc(e.slot.name) + ' — ' + money(e.pick.price) + '">' +
        '<span class="budget-seg__label">' + esc(e.slot.name) + '</span></span>';
    }).join('');
    var used = plan.filter(function (e) { return e.included && e.pick; })
      .reduce(function (a, e) { return a + e.pick.price; }, 0);
    if (denom > 0 && used < denom) {
      var rest = ((denom - used) / denom) * 100;
      html += '<span class="budget-seg budget-seg--rest" style="width:' + rest.toFixed(2) + '%" title="Unspent"></span>';
    }
    segBar.innerHTML = html;
  }

  function updateTotals() {
    var total = planTotal();
    totalEl.textContent = money(total);
    var pct = budgetCap > 0 ? Math.min(100, (total / budgetCap) * 100) : 0;
    fillEl.style.width = pct + '%';
    totalBar.classList.toggle('is-over', total > budgetCap);
    renderSegments();
    var count = plan.filter(function (e) { return e.included && e.pick; }).length;
    if (saveBtn) saveBtn.disabled = count === 0;
  }

  /* --------------------------- Save --------------------------- */
  if (saveBtn) {
    saveBtn.addEventListener('click', function () {
      var chosen = plan.filter(function (e) { return e.included && e.pick; });
      if (!chosen.length || !window.MSGWishlist) return;
      var folderName = room.label + ' — ' + money0(budgetCap) + ' Budget';
      chosen.forEach(function (e) {
        var p = e.pick;
        window.MSGWishlist.add(
          { id: p.id, title: p.title, url: p.url, image: p.image, price: p.price },
          folderName
        );
      });
      if (saveNoteEl) {
        saveNoteEl.hidden = false;
        saveNoteEl.innerHTML = 'Saved ' + chosen.length + ' piece' + (chosen.length > 1 ? 's' : '') +
          ' to your <a href="/pages/wishlist">wishlist</a>, under &ldquo;' + esc(folderName) + '.&rdquo;';
      }
    });
  }

  log('ready');
})();
